
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isAfter } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { parseDateFromUrl } from '@/utils/dateParser';
import { useCalendarDateLogic } from '@/hooks/useCalendarDateLogic';
import { useCalendarDynamicText } from '@/hooks/useCalendarDynamicText';
import CalendarHeader from '@/components/calendar/CalendarHeader';
import CalendarMonth from '@/components/calendar/CalendarMonth';

interface PropertyCalendarProps {
  unavailableDates?: Date[];
  onDateChange?: (checkIn: Date | undefined, checkOut: Date | undefined) => void;
  selectedCheckIn?: Date | undefined;
  selectedCheckOut?: Date | undefined;
}

const PropertyCalendar = ({ 
  unavailableDates = [], 
  onDateChange, 
  selectedCheckIn, 
  selectedCheckOut 
}: PropertyCalendarProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(undefined);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(undefined);
  const [isSelectingCheckOut, setIsSelectingCheckOut] = useState(false);
  const isMobile = useIsMobile();

  console.log('📅 PropertyCalendar unavailable dates:', unavailableDates.map(d => d.toDateString()));

  const {
    currentDate,
    setCurrentDate,
    memoizedCalendarData,
    isDateUnavailable,
    isDateSelected
  } = useCalendarDateLogic({ unavailableDates, isMobile });

  const dynamicTexts = useCalendarDynamicText({ checkInDate, checkOutDate });

  // Initialize from URL parameters on component mount
  useEffect(() => {
    const checkinParam = searchParams.get('checkin');
    const checkoutParam = searchParams.get('checkout');

    console.log('📅 PropertyCalendar URL params:', { 
      checkin: checkinParam, 
      checkout: checkoutParam 
    });

    if (checkinParam) {
      const checkinDate = parseDateFromUrl(checkinParam);
      if (checkinDate) {
        setCheckInDate(checkinDate);
        console.log('✅ PropertyCalendar set check-in from URL:', checkinDate);
      }
    }

    if (checkoutParam) {
      const checkoutDate = parseDateFromUrl(checkoutParam);
      if (checkoutDate) {
        setCheckOutDate(checkoutDate);
        console.log('✅ PropertyCalendar set check-out from URL:', checkoutDate);
      }
    }
  }, [searchParams]);

  // Sync with external selected dates when they change
  useEffect(() => {
    if (selectedCheckIn !== checkInDate) {
      setCheckInDate(selectedCheckIn);
      console.log('🔄 PropertyCalendar syncing external check-in:', selectedCheckIn);
    }
  }, [selectedCheckIn]);

  useEffect(() => {
    if (selectedCheckOut !== checkOutDate) {
      setCheckOutDate(selectedCheckOut);
      console.log('🔄 PropertyCalendar syncing external check-out:', selectedCheckOut);
    }
  }, [selectedCheckOut]);

  // Reset selection state when both dates are cleared
  useEffect(() => {
    if (!checkInDate && !checkOutDate) {
      setIsSelectingCheckOut(false);
      console.log('🔄 PropertyCalendar reset selection state');
    }
  }, [checkInDate, checkOutDate]);

  const handleDateClick = useCallback((date: Date) => {
    if (isDateUnavailable(date)) return;

    if (!checkInDate || (checkInDate && checkOutDate) || !isSelectingCheckOut) {
      // Select check-in
      setCheckInDate(date);
      setCheckOutDate(undefined);
      setIsSelectingCheckOut(true);
      onDateChange?.(date, undefined);
      console.log('📅 PropertyCalendar selected check-in:', date);
    } else if (checkInDate && isSelectingCheckOut) {
      // Select check-out
      if (isAfter(date, checkInDate)) {
        setCheckOutDate(date);
        setIsSelectingCheckOut(false);
        onDateChange?.(checkInDate, date);
        console.log('📅 PropertyCalendar selected check-out:', date);
      } else {
        // Reset if selecting earlier date
        setCheckInDate(date);
        setCheckOutDate(undefined);
        setIsSelectingCheckOut(true);
        onDateChange?.(date, undefined);
        console.log('📅 PropertyCalendar reset to new check-in:', date);
      }
    }
  }, [checkInDate, checkOutDate, isSelectingCheckOut, isDateUnavailable, onDateChange]);

  const clearDates = useCallback(() => {
    setCheckInDate(undefined);
    setCheckOutDate(undefined);
    setIsSelectingCheckOut(false);
    
    // Clear URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('checkin');
    newSearchParams.delete('checkout');
    
    // Update URL without the date parameters
    navigate(`${window.location.pathname}?${newSearchParams.toString()}`, { replace: true });
    
    // Notify parent component to also clear dates in BookingCard
    onDateChange?.(undefined, undefined);
    console.log('🗑️ PropertyCalendar cleared dates and URL parameters');
  }, [searchParams, navigate, onDateChange]);

  const getDayClassName = useCallback((day: Date, isCurrentMonth: boolean) => {
    const isUnavailable = isDateUnavailable(day);
    const selectionType = isDateSelected(day, checkInDate, checkOutDate);
    
    let className = 'h-10 w-10 text-sm rounded-full transition-colors relative ';
    
    if (!isCurrentMonth) {
      className += 'text-gray-300 cursor-default ';
    } else if (isCurrentMonth && !isUnavailable) {
      className += 'hover:bg-gray-100 cursor-pointer ';
    }
    
    if (isUnavailable) {
      className += 'text-gray-300 cursor-not-allowed line-through ';
    }
    
    if (selectionType === 'start' || selectionType === 'end') {
      className += 'bg-black text-white ';
    } else if (selectionType === 'range') {
      className += 'bg-gray-200 ';
    }
    
    return className;
  }, [isDateUnavailable, isDateSelected, checkInDate, checkOutDate]);

  return (
    <div className="py-6 border-b border-gray-200" data-calendar-section>
      <CalendarHeader
        title={dynamicTexts.title}
        subtext={dynamicTexts.subtext}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        onClearDates={clearDates}
        isMobile={isMobile}
      />

      <div className={`flex ${isMobile ? 'flex-col' : 'space-x-8'}`}>
        <CalendarMonth
          monthDate={memoizedCalendarData.currentMonth}
          days={memoizedCalendarData.currentMonthDays}
          onDateClick={handleDateClick}
          getDayClassName={getDayClassName}
          isDateUnavailable={isDateUnavailable}
        />
        {!isMobile && (
          <CalendarMonth
            monthDate={memoizedCalendarData.nextMonth}
            days={memoizedCalendarData.nextMonthDays}
            onDateClick={handleDateClick}
            getDayClassName={getDayClassName}
            isDateUnavailable={isDateUnavailable}
          />
        )}
      </div>

      {/* Mobile clear dates button - below calendar */}
      {isMobile && (checkInDate || checkOutDate) && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" size="sm" onClick={clearDates} className="text-sm">
            Limpiar fechas
          </Button>
        </div>
      )}
    </div>
  );
};

export default PropertyCalendar;
